import { Component, OnDestroy, OnInit } from '@angular/core';
import { AlliancesService } from '@core/services/alliances.service';
import { AnalyticsService } from '@core/services/analytics.service';
import { select, Store } from '@ngrx/store';
import { IFilters } from '@results/store/results.state';
import { UrlService } from '@services/url.service';
import { selectContextCore, selectContextLanding } from '@store/context/context.selector';
import { IContextCore } from '@store/context/context.state';
import { IHotelsAppState } from '@store/index';
import { getNested } from 'globals';
import { Observable, Subject } from 'rxjs';
import { delay, filter, take, takeUntil, tap } from 'rxjs/operators';
import {
  selectCustomBanner,
  selectError,
  selectFilterQueryParams,
  selectFilters,
  selectFiltersList,
  selectHotels,
  selectLoading,
  selectPagination,
  selectViewMap,
} from '@results/store/results.selector';
import { selectRouterEntity } from '@store/router/router.selector';

@Component({ template: `` })
export class ResultsBaseComponent implements OnInit, OnDestroy {
  public error$: Observable<any>;
  public context$: Observable<IContextCore>;
  public filters$: Observable<IFilters>;
  public hotels$: Observable<any>;
  public customBanner$: Observable<any>;
  public entity$: Observable<any>;
  public loading$: Observable<boolean>;
  public showHotelsIn$: Observable<boolean>;
  public pagination$: Observable<any>;
  public viewMap$: Observable<boolean>;
  public isLanding$: Observable<boolean>;
  public unSubscribe$ = new Subject<void>();
  public showFilters = false;
  public expandedMap = false;
  public disneySearch = false;
  public universalSearch = false;
  public viewInit = false;
  public occupancy: number;
  public showBannerNowTwelve: boolean;
  public argId = '1123215';
  public isAlliance: boolean;
  public seo: any;
  public listFilter: [];
  public brands: string[] = ['almundo', 'avantrip'];
  public showBannerDisney = false;
  public isExpert = false;
  public isArgentina = false;
  public hasSeo = false;
  constructor(
    protected store$: Store<IHotelsAppState>,
    protected allianceService: AlliancesService,
    protected analyticsService: AnalyticsService,
    protected urlService: UrlService,
  ) {}

  public ngOnInit(): void {
    this.error$ = this.store$.pipe(select(selectError));

    this.context$ = this.store$.pipe(select(selectContextCore));

    this.store$
      .pipe(
        select(selectFiltersList),
        filter((listFilter) => !!listFilter),
        take(1),
      )
      .subscribe((listFilter) => {
        this.listFilter = listFilter;
      });

    this.filters$ = this.store$.pipe(select(selectFilters));

    this.entity$ = this.store$.pipe(
      select(selectRouterEntity),
      tap((entity) => this.checkDisneyUniversalSearch(entity.id)),
    );
    this.showHotelsIn$ = this.store$.pipe(select(selectFilterQueryParams));
    this.loading$ = this.store$.pipe(select(selectLoading));
    this.hotels$ = this.store$.pipe(select(selectHotels));
    this.hotels$
      .pipe(
        takeUntil(this.unSubscribe$),
        filter((hotels: any) => hotels?.length > 0),
      )
      .subscribe((hotels: Array<any>) => {
        const countryId = hotels[0].country;
        this.showNowTwelve(countryId);
      });
    this.customBanner$ = this.store$.pipe(select(selectCustomBanner));
    this.pagination$ = this.store$.pipe(
      select(selectPagination),
      delay(0),
      tap((pagination) => this.checkOccupancy(pagination)),
    );
    this.store$.pipe(select(selectLoading), takeUntil(this.unSubscribe$)).subscribe((loading) => {
      const mbProgressBar = document.querySelector('mb-progress-bar') as HTMLElement;
      mbProgressBar.style.display = loading ? 'block' : 'none';
    });
    this.viewMap$ = this.store$.pipe(select(selectViewMap));
    this.isAlliance = this.allianceService.isAlliance();
    this.setSeo();
    this.setIsLanding();
    this.isExpert = this.urlService.isExperts();
    this.context$.pipe(takeUntil(this.unSubscribe$)).subscribe((context) => {
      if (this.brands.includes(context.brand)) {
        this.showBannerDisney = true;
      } else {
        this.showBannerDisney = false;
      }
    });
    this.hasSeo = Boolean(this.seo?.destination?.name || this.seo?.destination?.description);
  }

  public toggleExpandedMap() {
    this.expandedMap = !this.expandedMap;
    window.scrollTo(0, 0);
  }

  public checkDisneyUniversalSearch(entity_id) {
    const disney_city = '2359298';
    const universal_city = 'new6280590';
    this.disneySearch = entity_id === disney_city;
    this.universalSearch = entity_id === universal_city;
  }

  public checkOccupancy(pagination) {
    const totalHotels = pagination.hotel_amount;
    const availableHotels = pagination.total * 100;
    const cityAvailability = availableHotels / totalHotels;
    this.occupancy = Math.ceil(100 - cityAvailability);
  }

  public trackContactRowClick() {
    this.analyticsService.trackEvent({
      action: 'Contact Row',
      label: 'Click',
      section: 'Results',
    });
  }

  protected showNowTwelve(countryId: string) {
    this.isArgentina = countryId === this.argId;
    let visible;
    this.store$.pipe(select(selectContextCore), take(1)).subscribe((context) => {
      const flexibleRate = context.company !== 'cvc';
      const isArg = context.site === 'arg';
      visible = flexibleRate && isArg;
    });
    this.showBannerNowTwelve = this.isArgentina && visible;
  }

  protected setIsLanding() {
    this.isLanding$ = this.store$.pipe(select(selectContextLanding), takeUntil(this.unSubscribe$));
    this.isLanding$.pipe(takeUntil(this.unSubscribe$)).subscribe((isLanding) => {
      this.hasSeo =
        isLanding && Boolean(this.seo?.destination?.name || this.seo?.destination?.description);
    });
  }

  protected setSeo() {
    this.seo = getNested(window, '_app_context', 'seo');
  }

  public ngOnDestroy() {
    this.unSubscribe$.next();
    this.unSubscribe$.unsubscribe();
  }
}
